/**
 * Owner signature block — name, signature line, and date.
 * Exports both a react-pdf version (for PDF generation) and an HTML version (for screen preview).
 */
import { StyleSheet, Text, View } from '@react-pdf/renderer'

import type { Member } from '@snootsvet/shared'

import {
  FillField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { mm } from '@/frontend/features/printing/pdf-utils'

interface OwnerSignatureProps {
  owner: Member
  /** Label for the name line. Defaults to "Owner name". */
  label?: string
  /** Whether to pre-fill the name line with the owner's name. Defaults to true. */
  prefillName?: boolean
}

const styles = StyleSheet.create({
  bold: { fontFamily: 'Helvetica-Bold' },
})

export function OwnerSignaturePreview({
  owner,
  label = 'Owner name',
  prefillName = true,
}: OwnerSignatureProps) {
  const name = prefillName ? `${owner.firstName} ${owner.lastName}` : undefined

  return (
    <div className="mt-8 space-y-4">
      <p>
        <strong>{label}:</strong> {name ?? '____________________________'}
      </p>
      <p className="pt-2">
        <strong>Signature:</strong> ____________________________
      </p>
      <p>
        <strong>Date:</strong> ____________________________
      </p>
    </div>
  )
}

export function OwnerSignature({
  owner,
  label = 'Owner name',
  prefillName = true,
}: OwnerSignatureProps) {
  const name = prefillName ? `${owner.firstName} ${owner.lastName}` : undefined

  return (
    <View wrap={false} style={{ marginTop: mm(8) }}>
      <WithInlineResponse response={<FillField value={name} />}>
        <Text style={styles.bold}>{label}:</Text>
      </WithInlineResponse>
      <Spacer height={mm(6)} />
      <WithInlineResponse response={<FillField />}>
        <Text style={styles.bold}>Signature:</Text>
      </WithInlineResponse>
      <Spacer height={mm(4)} />
      <WithInlineResponse response={<FillField />}>
        <Text style={styles.bold}>Date:</Text>
      </WithInlineResponse>
    </View>
  )
}
